import { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';

const SILENCE_THRESHOLD = 0.01;
const SILENCE_DURATION = 1500;

/**
 * MicButton — Speech-to-text button for any text input.
 * Click to start recording, click again to stop. Auto-stops after 1.5s silence.
 * Transcribes via Deepgram and calls onTranscript with the text.
 *
 * Props:
 *   onTranscript(text)  — called with transcribed text
 *   className           — optional additional classes
 *   size                — icon size (default 16)
 */
export default function MicButton({ onTranscript, className = '', size = 16 }) {
  const [status, setStatus] = useState('idle'); // idle | listening | transcribing
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const silenceRafRef = useRef(null);
  const audioCtxRef = useRef(null);
  // Set when the button unmounts so recording stops and no audio is uploaded.
  const unmountedRef = useRef(false);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      stopRecording();
    };
  }, []);

  async function toggleMic() {
    if (status === 'listening') {
      stopRecording();
      return;
    }
    if (status !== 'idle') return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (unmountedRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      audioStreamRef.current = stream;
      audioChunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => handleRecordingDone();
      recorder.start(250);
      setStatus('listening');

      // Silence detection
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const data = new Float32Array(analyser.fftSize);
      let silentSince = null;

      function check() {
        if (status !== 'listening' && !mediaRecorderRef.current) return;
        analyser.getFloatTimeDomainData(data);
        let rms = 0;
        for (let i = 0; i < data.length; i++) rms += data[i] * data[i];
        rms = Math.sqrt(rms / data.length);

        if (rms < SILENCE_THRESHOLD) {
          if (!silentSince) silentSince = Date.now();
          else if (Date.now() - silentSince > SILENCE_DURATION) {
            stopRecording();
            return;
          }
        } else {
          silentSince = null;
        }
        silenceRafRef.current = requestAnimationFrame(check);
      }
      check();
    } catch (err) {
      console.error('Microphone access denied:', err);
      setStatus('idle');
    }
  }

  function stopRecording() {
    if (silenceRafRef.current) cancelAnimationFrame(silenceRafRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
      audioStreamRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
  }

  async function handleRecordingDone() {
    if (unmountedRef.current) { audioChunksRef.current = []; return; }
    const chunks = audioChunksRef.current;
    if (!chunks.length) { setStatus('idle'); return; }
    const blob = new Blob(chunks, { type: chunks[0]?.type || 'audio/webm' });
    if (blob.size < 1000) { setStatus('idle'); return; }

    setStatus('transcribing');
    try {
      const form = new FormData();
      form.append('audio', blob, 'voice-input.webm');

      const token = localStorage.getItem('mickey_token');
      const res = await fetch('/api/transcribe', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      const data = await res.json();
      if (data.transcript?.trim()) {
        onTranscript(data.transcript.trim());
      }
    } catch (err) {
      console.error('Transcription failed:', err);
    } finally {
      setStatus('idle');
    }
  }

  return (
    <button
      type="button"
      onClick={toggleMic}
      disabled={status === 'transcribing'}
      className={`inline-flex items-center justify-center rounded-md border-none p-1.5 cursor-pointer transition-all duration-200
        ${status === 'listening'
          ? 'bg-red-500 text-white shadow-[0_0_0_3px_rgba(239,68,68,0.3)] scale-110'
          : status === 'transcribing'
            ? 'bg-amber-500 text-white opacity-70 animate-pulse'
            : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600 hover:text-gray-700 dark:hover:text-gray-200'
        } ${className}`}
      title={status === 'idle' ? 'Click to speak' : status === 'listening' ? 'Click to stop' : 'Transcribing...'}
    >
      {status === 'transcribing' ? <Loader2 size={size} className="animate-spin" /> :
       status === 'listening' ? <MicOff size={size} /> :
       <Mic size={size} />}
    </button>
  );
}
